import { hydrateRoot, createRoot } from 'react-dom/client';
import { QueryClientProvider, QueryClient, hydrate, useQueryClient, useQuery } from '@tanstack/react-query';
import { createContext, useContext, Component, useState, useEffect, useMemo, useRef } from 'react';
import { jsx, jsxs } from 'react/jsx-runtime';
import { cloneDeep, mergeWith, isPlainObject } from 'lodash-es';
import { z } from 'zod';

// src/runtime/mount.tsx
var PortalContainerContext = createContext(void 0);
var PortalContainerProvider = PortalContainerContext.Provider;
function usePortalContainer() {
  return useContext(PortalContainerContext);
}
var ShadowHostContext = createContext(null);
var ShadowHostProvider = ShadowHostContext.Provider;
function useShadowHost() {
  return useContext(ShadowHostContext);
}
function WidgetHydrationTree({
  queryClient,
  portalContainer,
  shadowHost,
  children
}) {
  return /* @__PURE__ */ jsx(PortalContainerProvider, { value: portalContainer, children: /* @__PURE__ */ jsx(ShadowHostProvider, { value: shadowHost ?? null, children: /* @__PURE__ */ jsx(QueryClientProvider, { client: queryClient, children }) }) });
}

// src/runtime/shadow-dom.ts
var CSS_LOAD_TIMEOUT_MS = 3e3;
function waitForShadowLinks(shadowRoot) {
  const links = Array.from(
    shadowRoot.querySelectorAll('link[rel="stylesheet"]')
  );
  return Promise.all(
    links.map(
      (link) => new Promise((resolve) => {
        let settled = false;
        const done = () => {
          if (settled) return;
          settled = true;
          resolve();
        };
        if (link.sheet) return done();
        link.onload = done;
        link.onerror = () => {
          console.warn(`[HRWidget] Failed to load CSS in shadow DOM: ${link.href}`);
          done();
        };
        const start = Date.now();
        const poll = () => {
          if (settled) return;
          if (link.sheet) return done();
          if (Date.now() - start >= CSS_LOAD_TIMEOUT_MS) return done();
          setTimeout(poll, 16);
        };
        setTimeout(poll, 0);
      })
    )
  ).then(() => void 0);
}
var SHADOW_SUPPORTED_TAGS = /* @__PURE__ */ new Set([
  "ARTICLE",
  "ASIDE",
  "BLOCKQUOTE",
  "BODY",
  "DIV",
  "FOOTER",
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  "HEADER",
  "MAIN",
  "NAV",
  "P",
  "SECTION",
  "SPAN"
]);
function canAttachShadow(el) {
  if (SHADOW_SUPPORTED_TAGS.has(el.tagName)) return true;
  if (el.tagName.includes("-")) return true;
  return false;
}
function setupShadowDOM(hostEl, cssUrls) {
  try {
    let shadowHost = hostEl;
    if (!canAttachShadow(hostEl)) {
      const wrapper = document.createElement("div");
      hostEl.parentNode?.insertBefore(wrapper, hostEl);
      wrapper.appendChild(hostEl);
      shadowHost = wrapper;
    }
    const shadowRoot = shadowHost.attachShadow({ mode: "open" });
    for (const url of cssUrls) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = url;
      shadowRoot.appendChild(link);
    }
    const reactRoot = document.createElement("div");
    reactRoot.className = "hr-react-root";
    shadowRoot.appendChild(reactRoot);
    const portalContainer = document.createElement("div");
    portalContainer.className = "hr-portal-container";
    shadowRoot.appendChild(portalContainer);
    const cssReady = waitForShadowLinks(shadowRoot);
    return { shadowRoot, shadowHost, reactRoot, portalContainer, cssReady, cssPending: true };
  } catch (e) {
    console.warn("[HRWidget] Shadow DOM setup failed, falling back to light DOM:", e);
    return null;
  }
}
function adoptDeclarativeShadowRoot(hostEl) {
  try {
    let ponyfilled = false;
    if (!hostEl.shadowRoot) {
      const tpl = hostEl.querySelector(
        ":scope > template[shadowrootmode]"
      );
      if (!tpl) return null;
      if (!canAttachShadow(hostEl)) return null;
      const sr = hostEl.attachShadow({ mode: "open" });
      sr.appendChild(tpl.content);
      tpl.remove();
      ponyfilled = true;
    }
    const shadowRoot = hostEl.shadowRoot;
    if (!shadowRoot) return null;
    const kids = Array.from(shadowRoot.children);
    const reactRoot = kids.find(
      (c) => c.classList.contains("hr-react-root")
    );
    if (!reactRoot) return null;
    let portalContainer = kids.find(
      (c) => c.classList.contains("hr-portal-container")
    );
    if (!portalContainer) {
      portalContainer = document.createElement("div");
      portalContainer.className = "hr-portal-container";
      shadowRoot.appendChild(portalContainer);
    }
    return {
      shadowRoot,
      shadowHost: hostEl,
      reactRoot,
      portalContainer,
      cssReady: ponyfilled ? waitForShadowLinks(shadowRoot) : Promise.resolve(),
      cssPending: ponyfilled
    };
  } catch (e) {
    console.warn("[HRWidget] DSD adoption failed, falling back:", e);
    return null;
  }
}
function getCSSUrlsFromElement(el) {
  const raw = el.getAttribute("data-hr-css");
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
function migrateSSRContent(hostEl, reactRoot) {
  while (hostEl.firstChild) {
    reactRoot.appendChild(hostEl.firstChild);
  }
}
function isShadowDOMSupported() {
  return typeof HTMLElement !== "undefined" && typeof HTMLElement.prototype.attachShadow === "function";
}

// src/runtime/host-theme.ts
var HOST_THEME_ATTR = "data-hr-theme";
var DARK_MQ = "(prefers-color-scheme: dark)";
function systemPrefersDark() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia(DARK_MQ).matches;
}
function resolveHostTheme(colorScheme, fallback = "light") {
  if (colorScheme === "dark" || colorScheme === "light") return colorScheme;
  if (colorScheme === "auto" || colorScheme === "global") {
    return systemPrefersDark() ? "dark" : "light";
  }
  return fallback;
}
function applyHostTheme(host, theme) {
  if (!host) return;
  if (host.getAttribute(HOST_THEME_ATTR) !== theme) {
    host.setAttribute(HOST_THEME_ATTR, theme);
  }
}
function attachHostThemeController(host, colorScheme, opts) {
  if (!host) return () => {
  };
  host.removeAttribute("data-hr-scheme");
  applyHostTheme(host, resolveHostTheme(colorScheme, opts?.fallback));
  const followsSystem = colorScheme === "auto" || colorScheme === "global";
  if (!followsSystem || typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {
    };
  }
  const mq = window.matchMedia(DARK_MQ);
  const onChange = () => {
    if (!host.isConnected) {
      mq.removeEventListener("change", onChange);
      return;
    }
    applyHostTheme(host, mq.matches ? "dark" : "light");
  };
  mq.addEventListener("change", onChange);
  return () => mq.removeEventListener("change", onChange);
}
function mirrorHostTheme(source, target) {
  const sync = () => {
    const value = source.getAttribute(HOST_THEME_ATTR);
    if (value === "light" || value === "dark") {
      applyHostTheme(target, value);
    } else {
      target.removeAttribute(HOST_THEME_ATTR);
    }
  };
  sync();
  if (typeof MutationObserver === "undefined") return () => {
  };
  const observer = new MutationObserver(sync);
  observer.observe(source, { attributes: true, attributeFilter: [HOST_THEME_ATTR] });
  return () => observer.disconnect();
}

// src/contracts.ts
var EXPLORER_BACKED_KEYWORDS = ["explorer", "search-bar", "search-results"];
var isExplorerBackedKeyword = (keyword) => EXPLORER_BACKED_KEYWORDS.includes(keyword);
var SPACING_SIDES = ["top", "right", "bottom", "left"];
var DEFAULT_MARGIN = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "auto" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "auto" }
};
var DEFAULT_PADDING = {
  top: { value: 0, unit: "px" },
  right: { value: 0, unit: "px" },
  bottom: { value: 0, unit: "px" },
  left: { value: 0, unit: "px" }
};
var DEFAULT_BASE_SPACING = {
  margin: DEFAULT_MARGIN,
  padding: DEFAULT_PADDING,
  width: { value: 0, unit: "auto" },
  maxWidth: { value: 100, unit: "%" }
};
var marginSideSchema = (unit) => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%", "auto"]).default(unit).describe("Unit for this side; auto centers")
}).default({ value: 0, unit });
var paddingSideSchema = () => z.object({
  value: z.number().default(0).describe("Length for this side"),
  unit: z.enum(["px", "%"]).default("px").describe("Unit for this side")
}).default({ value: 0, unit: "px" });
var marginBoxSchema = (defaults) => z.object({
  top: marginSideSchema(defaults.top.unit),
  right: marginSideSchema(defaults.right.unit),
  bottom: marginSideSchema(defaults.bottom.unit),
  left: marginSideSchema(defaults.left.unit)
}).default(defaults).describe("Outer margin");
var paddingBoxSchema = () => z.object({
  top: paddingSideSchema(),
  right: paddingSideSchema(),
  bottom: paddingSideSchema(),
  left: paddingSideSchema()
}).default(DEFAULT_PADDING).describe("Inner padding");
var widthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Width length"),
  unit: z.enum(["px", "%", "auto"]).default(defaults.unit).describe("Width unit; auto leaves width unset")
}).default(defaults).describe("Width of the element");
var maxWidthSchema = (defaults) => z.object({
  value: z.number().default(defaults.value).describe("Maximum width length"),
  unit: z.enum(["px", "%", "none"]).default(defaults.unit).describe("Maximum width unit; none removes the constraint")
}).default(defaults).describe("Maximum width of the element");
z.object({
  margin: marginBoxSchema(DEFAULT_MARGIN),
  padding: paddingBoxSchema(),
  width: widthSchema(DEFAULT_BASE_SPACING.width),
  maxWidth: maxWidthSchema(DEFAULT_BASE_SPACING.maxWidth)
}).default(DEFAULT_BASE_SPACING).describe("Margin, padding, width and maximum width of the widget");
function coerceSide(raw, extraUnit) {
  if (typeof raw === "number" || typeof raw === "string") {
    if (extraUnit && raw === extraUnit) return { value: 0, unit: extraUnit };
    const n2 = Number(raw);
    return Number.isFinite(n2) ? { value: n2, unit: "px" } : void 0;
  }
  if (!raw || typeof raw !== "object") return void 0;
  const side = raw;
  const n = Number(side.value);
  const unit = side.unit === "%" ? "%" : extraUnit && side.unit === extraUnit ? extraUnit : "px";
  return { value: Number.isFinite(n) ? n : 0, unit };
}
function coerceBox(raw, allowAuto) {
  if (!raw || typeof raw !== "object") return void 0;
  const box = raw;
  const out = {};
  for (const name of SPACING_SIDES) {
    const side = coerceSide(box[name], allowAuto ? "auto" : void 0);
    if (side) out[name] = side;
  }
  return out;
}
function coerceSpacingValue(raw) {
  if (!raw || typeof raw !== "object") return void 0;
  const input = raw;
  const out = { ...input };
  if ("margin" in input) {
    const margin = coerceBox(input.margin, true);
    if (margin) out.margin = margin;
    else delete out.margin;
  }
  if ("padding" in input) {
    const padding = coerceBox(input.padding, false);
    if (padding) out.padding = padding;
    else delete out.padding;
  }
  if ("width" in input) {
    const width = coerceSide(input.width, "auto");
    if (width) out.width = width;
    else delete out.width;
  }
  if ("maxWidth" in input) {
    const maxWidth = coerceSide(input.maxWidth, "none");
    if (maxWidth) out.maxWidth = maxWidth;
    else delete out.maxWidth;
  }
  return out;
}
function liftLegacySpacing(options) {
  if (!options || typeof options !== "object") return options;
  const hasSpacing = "spacing" in options;
  const hasLegacyWidth = "width" in options;
  const hasLegacyMargin = "margin" in options;
  const hasLegacyPadding = "padding" in options;
  if (!hasSpacing && !hasLegacyWidth && !hasLegacyMargin && !hasLegacyPadding) return options;
  const spacing = hasSpacing ? coerceSpacingValue(options.spacing) ?? {} : {};
  if (!("maxWidth" in spacing) && hasLegacyWidth) {
    const legacy = options.width;
    if (legacy && typeof legacy === "object" && "maxWidth" in legacy) {
      const n = Number(legacy.maxWidth);
      if (Number.isFinite(n)) {
        spacing.maxWidth = { value: n, unit: legacy.unit === "px" ? "px" : "%" };
      }
    }
  }
  if (!("margin" in spacing) && hasLegacyMargin) {
    const margin = coerceBox(options.margin, true);
    if (margin) spacing.margin = margin;
  }
  if (!("padding" in spacing) && hasLegacyPadding) {
    const padding = coerceBox(options.padding, false);
    if (padding) spacing.padding = padding;
  }
  return { ...options, spacing };
}

// src/runtime/api.ts
function homerunnerApiBaseUrl() {
  if (typeof window !== "undefined") {
    const fromRuntime = window.HRWidgetRuntime?.apiBaseUrl;
    if (fromRuntime) return fromRuntime;
  }
  const envValue = typeof process !== "undefined" ? process.env?.NEXT_PUBLIC_HOMERUNNER_BASE_URL : void 0;
  if (envValue) return envValue;
  return "https://central.homerunner.io";
}
var WidgetFetchError = class extends Error {
  constructor(status, message, retryAfterMs) {
    super(message);
    this.status = status;
    this.retryAfterMs = retryAfterMs;
    this.name = "WidgetFetchError";
  }
};
function isWidgetFetchError(error) {
  if (error instanceof WidgetFetchError) return true;
  if (typeof error !== "object" || error === null) return false;
  const e = error;
  return e.name === "WidgetFetchError" && typeof e.status === "number";
}
function isRateLimitError(error) {
  return isWidgetFetchError(error) && error.status === 429;
}
function isPermanentError(error) {
  return isWidgetFetchError(error) && error.status >= 400 && error.status < 500;
}
function retryTransient(max) {
  return (failureCount, error) => !isPermanentError(error) && failureCount < max;
}
var RATE_LIMIT_RETRY_CAP_MS = 2e4;
var RATE_LIMIT_BACKOFF_MS = [1e3, 2e3, 4e3];
function parseRetryAfterMs(headers) {
  const raw = headers?.get?.("Retry-After");
  if (!raw) return void 0;
  const seconds = Number(raw);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1e3);
  const at = Date.parse(raw);
  return Number.isNaN(at) ? void 0 : Math.max(0, at - Date.now());
}
function isRetryableRateLimit(error) {
  if (!isRateLimitError(error)) return false;
  const asked = error.retryAfterMs;
  return asked === void 0 || asked <= RATE_LIMIT_RETRY_CAP_MS;
}
function retryTransientOrThrottled(max, rateLimitMax = RATE_LIMIT_BACKOFF_MS.length) {
  return (failureCount, error) => {
    if (isRateLimitError(error)) {
      return isRetryableRateLimit(error) && failureCount < rateLimitMax;
    }
    return !isPermanentError(error) && failureCount < max;
  };
}
function retryDelayFor(failureCount, error) {
  if (isRateLimitError(error)) {
    const asked = error.retryAfterMs;
    if (asked !== void 0) return Math.min(asked, RATE_LIMIT_RETRY_CAP_MS);
    return RATE_LIMIT_BACKOFF_MS[Math.min(failureCount, RATE_LIMIT_BACKOFF_MS.length - 1)];
  }
  return Math.min(1e3 * 2 ** failureCount, 3e4);
}
async function fetchFeed(feedId) {
  const res = await fetch(`${homerunnerApiBaseUrl()}/public-api/v1/${feedId}/details`);
  if (!res.ok) {
    throw new WidgetFetchError(
      res.status,
      `Failed to fetch feed (HTTP ${res.status})`,
      parseRetryAfterMs(res.headers)
    );
  }
  const body = await res.json();
  if (body.status_code !== 200) {
    throw new WidgetFetchError(body.status_code, "Failed to fetch feed");
  }
  return body.result;
}
async function fetchWidget(widgetId) {
  const res = await fetch(`${homerunnerApiBaseUrl()}/public-api/v1/widget/${widgetId}`);
  if (!res.ok) {
    throw new WidgetFetchError(
      res.status,
      `Failed to fetch widget (HTTP ${res.status})`,
      parseRetryAfterMs(res.headers)
    );
  }
  const body = await res.json();
  if (!body.success) {
    throw new WidgetFetchError(res.status, "Failed to fetch widget");
  }
  return body.data;
}
async function fetchPropertyBySlug(feedId, slug) {
  const url = new URL(`${homerunnerApiBaseUrl()}/public-api/v1/property-details`);
  url.searchParams.set("feed_id", String(feedId));
  url.searchParams.set("property", slug);
  const res = await fetch(url.toString());
  if (!res.ok) {
    throw new WidgetFetchError(
      res.status,
      `Failed to resolve property "${slug}" in feed ${feedId} (HTTP ${res.status})`,
      parseRetryAfterMs(res.headers)
    );
  }
  const body = await res.json();
  return body.result ?? {};
}
function getFinalSettings(widget, feed, optionsOverride) {
  const parentColorScheme = optionsOverride?.__parentColorScheme;
  let overrideRest;
  if (optionsOverride) {
    overrideRest = cloneDeep(optionsOverride);
    delete overrideRest.__parentColorScheme;
  }
  const explorerGlobal = isExplorerBackedKeyword(widget.keyword) ? feed.additional_info?.explorer ?? {} : {};
  const bookingGlobal = widget.keyword === "booking" ? feed.additional_info?.booking ?? {} : {};
  const finalOptions = mergeWith(
    {},
    {
      colorScheme: feed.additional_info?.branding?.colorScheme,
      lightModeColors: {
        accent: feed.additional_info?.branding?.lightModeColors?.accent
      },
      darkModeColors: {
        accent: feed.additional_info?.branding?.darkModeColors?.accent
      },
      font: {
        size: feed.additional_info?.branding?.font?.size,
        family: feed.additional_info?.branding?.font?.family
      }
    },
    explorerGlobal,
    bookingGlobal,
    cloneDeep(widget.settings),
    overrideRest,
    // #743: arrays are atomic values here — a higher-priority array replaces
    // the lower one wholesale. Lodash's default merges arrays by index, which
    // let a shorter override keep the default's tail entries (e.g. stored
    // primaryFilters ['where'] over the 4-entry default resurrected the three
    // unchecked filters). Cloned so the result never aliases feed.additional_info.
    //
    // #1703: PHP's json_encode spells an empty object as `[]`, so a settings
    // GROUP the dashboard saved as `{}` (an untouched lenient group: gallery
    // `rooms`, related-properties `card`, explorer `searchbar`, …) reaches
    // this merge as `[]`. Lodash merges a higher-priority OBJECT into an
    // array destination as own properties of that array — still an array to
    // every group parser (`phpObject`, `lenientObjectGroup`,
    // `stripNullSettingLeaves`), which falls back to the group's defaults —
    // so a per-embed `data-hr-options` override of such a group was silently
    // dropped. An object and an array never merge into each other here: an
    // object over an array replaces it (the higher layer wins outright, #673),
    // and an EMPTY array over an object is that same `{}` — it adds nothing,
    // so the lower layer's group stays (a feed global beneath an untouched
    // widget group). A non-empty array still replaces whatever sits below it.
    (objValue, srcValue) => {
      if (Array.isArray(srcValue)) {
        return srcValue.length === 0 && isPlainObject(objValue) ? objValue : cloneDeep(srcValue);
      }
      if (Array.isArray(objValue) && isPlainObject(srcValue)) return cloneDeep(srcValue);
      return void 0;
    }
  );
  const isConcreteScheme = (v) => v === "light" || v === "dark" || v === "auto";
  const overrideColorScheme = overrideRest?.colorScheme;
  const ownColorScheme = widget.settings?.colorScheme;
  const hasOverrideScheme = overrideColorScheme !== void 0 && overrideColorScheme !== null;
  const effectiveRawScheme = hasOverrideScheme ? overrideColorScheme : ownColorScheme;
  if (!isConcreteScheme(effectiveRawScheme)) {
    const inherited = parentColorScheme && parentColorScheme !== "global" ? parentColorScheme : feed.additional_info?.branding?.colorScheme;
    finalOptions.colorScheme = inherited ?? "light";
  }
  return liftLegacySpacing({
    ...finalOptions,
    customCss: finalOptions.customCss?.length ? finalOptions.customCss : ""
  });
}

// src/runtime/dehydrated-state.ts
var HR_COMPACT_REF_KEY = "$hr";
var HR_COMPACT_VERSION = 1;
var MIN_SHARED_BYTES = 32;
var hasOwn = (o, k) => Object.prototype.hasOwnProperty.call(o, k);
var isPlainObject2 = (v) => {
  if (v === null || typeof v !== "object" || Array.isArray(v)) return false;
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};
var isRef = (v) => isPlainObject2(v) && typeof v[HR_COMPACT_REF_KEY] === "number";
function isCompactDehydratedState(state) {
  if (!isPlainObject2(state)) return false;
  const compact = state.hrCompact;
  return isPlainObject2(compact) && compact.v === HR_COMPACT_VERSION && Array.isArray(compact.shared) && Array.isArray(compact.queries);
}
function dedupeDehydratedState(state, opts = {}) {
  const min = opts.minSharedBytes ?? MIN_SHARED_BYTES;
  const queries = state.queries ?? [];
  if (queries.length === 0) return state;
  const jsonCache = /* @__PURE__ */ new WeakMap();
  const canonical = (node) => {
    let text = jsonCache.get(node);
    if (text === void 0) {
      text = JSON.stringify(node);
      if (text !== void 0) jsonCache.set(node, text);
    }
    return text;
  };
  const keyOf = (node) => {
    if (typeof node === "string") return node.length >= min ? "s:" + node : void 0;
    if (Array.isArray(node) || isPlainObject2(node)) {
      const text = canonical(node);
      return text !== void 0 && text.length >= min ? "j:" + text : void 0;
    }
    return void 0;
  };
  const isOpaque = (node) => typeof node.toJSON === "function";
  const counts = /* @__PURE__ */ new Map();
  let unsafe = false;
  const count = (node) => {
    if (unsafe) return;
    if (typeof node === "string") {
      const key = keyOf(node);
      if (key !== void 0) counts.set(key, (counts.get(key) ?? 0) + 1);
      return;
    }
    if (Array.isArray(node)) {
      const key = keyOf(node);
      if (key !== void 0) counts.set(key, (counts.get(key) ?? 0) + 1);
      for (const child of node) count(child);
      return;
    }
    if (isPlainObject2(node)) {
      if (hasOwn(node, HR_COMPACT_REF_KEY)) {
        unsafe = true;
        return;
      }
      if (isOpaque(node)) return;
      const key = keyOf(node);
      if (key !== void 0) counts.set(key, (counts.get(key) ?? 0) + 1);
      for (const k of Object.keys(node)) count(node[k]);
    }
  };
  for (const q of queries) count(q.state.data);
  if (unsafe) return state;
  const entries = [];
  const entryIds = /* @__PURE__ */ new Map();
  const refCounts = [];
  const intern = (node) => {
    if (typeof node === "string") {
      const key = keyOf(node);
      return key !== void 0 && (counts.get(key) ?? 0) > 1 ? share(key, () => node) : node;
    }
    if (Array.isArray(node)) {
      const key = keyOf(node);
      const build = () => node.map(intern);
      return key !== void 0 && (counts.get(key) ?? 0) > 1 ? share(key, build) : build();
    }
    if (isPlainObject2(node)) {
      if (isOpaque(node)) return node;
      const key = keyOf(node);
      const build = () => {
        const out = [];
        for (const k of Object.keys(node)) {
          const v = node[k];
          if (v !== void 0) out.push([k, intern(v)]);
        }
        return Object.fromEntries(out);
      };
      return key !== void 0 && (counts.get(key) ?? 0) > 1 ? share(key, build) : build();
    }
    return node;
  };
  const share = (key, build) => {
    let id = entryIds.get(key);
    if (id === void 0) {
      id = entries.length;
      entryIds.set(key, id);
      entries.push(void 0);
      refCounts.push(0);
      entries[id] = build();
    }
    refCounts[id]++;
    return { [HR_COMPACT_REF_KEY]: id };
  };
  const interned = queries.map((q) => ({ ...q, state: { ...q.state, data: intern(q.state.data) } }));
  const shared = [];
  const renumbered = /* @__PURE__ */ new Map();
  const emit = (node) => {
    if (Array.isArray(node)) return node.map(emit);
    if (isPlainObject2(node)) {
      if (isRef(node)) {
        const id = node[HR_COMPACT_REF_KEY];
        if (refCounts[id] < 2) return emit(entries[id]);
        let n = renumbered.get(id);
        if (n === void 0) {
          n = shared.length;
          renumbered.set(id, n);
          shared.push(void 0);
          shared[n] = emit(entries[id]);
        }
        return { [HR_COMPACT_REF_KEY]: n };
      }
      return Object.fromEntries(Object.keys(node).map((k) => [k, emit(node[k])]));
    }
    return node;
  };
  const compactQueries = interned.map((q) => ({ ...q, state: { ...q.state, data: emit(q.state.data) } }));
  if (shared.length === 0) return state;
  return {
    mutations: state.mutations,
    queries: [],
    hrCompact: { v: HR_COMPACT_VERSION, shared, queries: compactQueries }
  };
}
function inflateDehydratedState(state) {
  if (!isCompactDehydratedState(state)) return state;
  const { shared, queries } = state.hrCompact;
  const resolve = (node) => {
    if (Array.isArray(node)) return node.map(resolve);
    if (isPlainObject2(node)) {
      if (isRef(node)) {
        const id = node[HR_COMPACT_REF_KEY];
        if (!Number.isInteger(id) || id < 0 || id >= shared.length) {
          throw new Error(`[widget] dehydrated state: dangling shared reference #${id} (${shared.length} entries)`);
        }
        return resolve(shared[id]);
      }
      return Object.fromEntries(Object.keys(node).map((k) => [k, resolve(node[k])]));
    }
    return node;
  };
  return {
    mutations: state.mutations ?? [],
    queries: queries.map((q) => ({ ...q, state: { ...q.state, data: resolve(q.state.data) } }))
  };
}

// src/runtime/query-client.ts
function getSharedQueryClient() {
  if (typeof window === "undefined") {
    return new QueryClient();
  }
  if (window.HRWidget?.__QUERY_CLIENT__) {
    return window.HRWidget.__QUERY_CLIENT__;
  }
  if (!window.HRWidget) {
    window.HRWidget = {};
  }
  window.HRWidget.__QUERY_CLIENT__ = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1e3,
        // 5 minutes — data is fresh for this long, no background refetch
        refetchOnWindowFocus: false,
        // don't refetch when user switches back to tab
        refetchOnReconnect: false,
        // don't refetch on network reconnect
        refetchOnMount: false,
        // don't refetch if data exists and isn't stale
        // Retry only transient failures (network, 5xx) up to TanStack's default
        // 3 attempts; never retry a permanent 4xx — a bad key / 404 won't fix
        // itself and retrying just stretches the widget's loading spinner
        // (#740/#741). The one exception is a 429 (#1930): the per-IP bucket
        // refills within the minute, so it is retried on the rate-limit ladder
        // (or the server's Retry-After, capped) before the notice shows.
        // Fetches that throw the status-aware WidgetFetchError get this for free.
        retry: retryTransientOrThrottled(3),
        retryDelay: retryDelayFor
      }
    }
  });
  if (window.HRWidget.__REACT_QUERY_STATE__) {
    let ssrState = null;
    try {
      ssrState = inflateDehydratedState(window.HRWidget.__REACT_QUERY_STATE__);
    } catch (err) {
      console.warn("[widget] SSR state could not be inflated; widgets fetch client-side.", err);
    }
    if (ssrState) hydrate(window.HRWidget.__QUERY_CLIENT__, ssrState);
  }
  return window.HRWidget.__QUERY_CLIENT__;
}

// src/utils/preload-hints.ts
var DEFAULT_MIN_HEIGHTS = {
  explorer: (options) => {
    const o = options;
    if (o?.search?.heroBar) return "140px";
    if (o?.template === "slider") return "590px";
    return "min(65vh, 640px)";
  },
  "search-bar": "138px",
  booking: "clamp(0px, (100vw - 960px) * 999, 280px)",
  calendar: "570px",
  "multi-calendar": "340px",
  "property-card": "370px",
  "related-properties": "440px",
  gallery: "260px"
};
function preloadMinHeightHint(widgetType, options) {
  const hint = DEFAULT_MIN_HEIGHTS[widgetType];
  if (!hint) return null;
  return typeof hint === "function" ? hint(options) : hint;
}

// src/runtime/preload-skeleton.ts
var C = "hr-preload";
var TYPE_VARIANTS = {
  "search-bar": "bar",
  "destination-search": "bar",
  booking: "form",
  calendar: "calendar",
  "multi-calendar": "calendar",
  "property-card": "card",
  "related-properties": "card-row",
  gallery: "gallery"
};
function readPath(options, path) {
  let cur = options;
  for (const key of path) {
    if (!cur || typeof cur !== "object") return void 0;
    cur = cur[key];
  }
  return cur;
}
function resolvePreloadSkeletonVariant(widgetType, options) {
  if (widgetType === "explorer") {
    if (readPath(options, ["search", "heroBar"])) return "bar";
    if (readPath(options, ["template"]) === "slider") return "card-row";
    return "grid";
  }
  return TYPE_VARIANTS[widgetType] ?? null;
}
var PRELOAD_SKELETON_CSS = [
  `.${C}{display:block;position:relative;width:100%;height:0;margin:0;padding:0;border:0;overflow:visible;}`,
  `.${C},.${C} *{box-sizing:border-box;}`,
  `.${C}__panel{position:absolute;top:0;left:0;right:0;height:var(--hr-preload-h,0);overflow:hidden;padding:20px;pointer-events:none;animation:hr-preload-pulse 1.6s ease-in-out infinite;-webkit-mask-image:linear-gradient(#000 calc(100% - 72px),transparent);mask-image:linear-gradient(#000 calc(100% - 72px),transparent);}`,
  `.${C}__panel--bar{display:flex;align-items:center;padding:0 20px;}`,
  // Bounded silhouettes (one bar, one card, one row) never overflow — no fade.
  `.${C}__panel--bar,.${C}__panel--card,.${C}__panel--card-row{-webkit-mask-image:none;mask-image:none;}`,
  `@keyframes hr-preload-pulse{0%,100%{opacity:1}50%{opacity:.55}}`,
  `@media (prefers-reduced-motion: reduce){.${C}__panel{animation:none;opacity:1;}}`,
  `.${C}__b,.${C}__img,.${C}__pill,.${C}__btn,.${C}__field,.${C}__tile,.${C}__ctl{background:rgba(128,128,128,.14);border-radius:8px;}`,
  `.${C}__line{height:12px;border-radius:6px;background:rgba(128,128,128,.11);}`,
  `.${C}__line--short{width:55%;}`,
  // grid + single row
  `.${C}__grid,.${C}__row{display:grid;gap:16px;grid-template-columns:repeat(var(--hr-preload-cols,1),minmax(0,1fr));}`,
  `.${C}__row{grid-template-rows:auto;grid-auto-rows:0;row-gap:0;overflow:hidden;}`,
  `@media (min-width:600px){.${C}__grid,.${C}__row{grid-template-columns:repeat(var(--hr-preload-cols-780,2),minmax(0,1fr));}}`,
  `@media (min-width:960px){.${C}__grid,.${C}__row{grid-template-columns:repeat(var(--hr-preload-cols-960,3),minmax(0,1fr));}}`,
  `.${C}__card{display:flex;flex-direction:column;gap:10px;min-width:0;}`,
  `.${C}__img{width:100%;aspect-ratio:16/10;max-height:220px;border-radius:12px;}`,
  `.${C}__card .${C}__line:first-of-type{margin-top:4px;}`,
  // single card
  `.${C}__panel--card .${C}__card{max-width:420px;}`,
  `.${C}__panel--card .${C}__img{aspect-ratio:4/3;max-height:280px;}`,
  // bar
  `.${C}__bar{display:flex;align-items:center;gap:10px;width:100%;height:64px;padding:8px;border-radius:999px;background:rgba(128,128,128,.08);}`,
  `.${C}__pill{flex:1 1 0;height:44px;min-width:0;border-radius:999px;}`,
  `.${C}__bar .${C}__btn{flex:0 0 112px;height:48px;border-radius:999px;background:rgba(128,128,128,.24);}`,
  `@media (max-width:599px){.${C}__bar .${C}__pill:nth-child(n+3){display:none;}.${C}__bar .${C}__btn{flex-basis:52px;}}`,
  // explorer's own filter row above its cards (`.hfse-form` then the grid, 20px apart)
  `.${C}__bar--filters{height:56px;margin-bottom:20px;}`,
  `.${C}__bar--filters .${C}__pill{height:40px;}`,
  `.${C}__bar--filters .${C}__btn{flex-basis:96px;height:40px;}`,
  // form
  `.${C}__form{display:flex;flex-direction:column;gap:12px;max-width:420px;}`,
  `.${C}__form-head{display:flex;align-items:baseline;gap:10px;margin-bottom:6px;}`,
  `.${C}__form-head .${C}__b:first-child{width:120px;height:24px;}`,
  `.${C}__form-head .${C}__b:last-child{width:64px;height:12px;}`,
  `.${C}__pair{display:grid;grid-template-columns:1fr 1fr;gap:12px;}`,
  `.${C}__field{height:48px;border-radius:10px;}`,
  `.${C}__form .${C}__btn{height:48px;margin-top:4px;border-radius:10px;background:rgba(128,128,128,.24);}`,
  `.${C}__fee{display:flex;justify-content:space-between;gap:16px;}`,
  `.${C}__fee .${C}__line{flex:0 0 45%;}`,
  `.${C}__fee .${C}__line:last-child{flex-basis:18%;}`,
  // calendar
  `.${C}__months{display:flex;gap:32px;}`,
  `.${C}__month{flex:1 1 0;max-width:380px;min-width:0;}`,
  `.${C}__month-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:14px;}`,
  `.${C}__ctl{width:28px;height:28px;border-radius:50%;}`,
  `.${C}__month-head .${C}__b{width:40%;height:16px;}`,
  `.${C}__week,.${C}__cells{display:grid;grid-template-columns:repeat(7,minmax(0,1fr));gap:6px;}`,
  `.${C}__week{margin-bottom:8px;}`,
  `.${C}__dow{height:8px;width:60%;margin:0 auto;border-radius:4px;background:rgba(128,128,128,.11);}`,
  `.${C}__cell{aspect-ratio:1;border-radius:50%;background:rgba(128,128,128,.12);}`,
  `.${C}__cell--off{background:transparent;}`,
  `@media (max-width:599px){.${C}__month+.${C}__month{display:none;}}`,
  // gallery
  `.${C}__gallery{display:grid;gap:8px;grid-template-columns:repeat(2,minmax(0,1fr));}`,
  `.${C}__tile{aspect-ratio:4/3;border-radius:10px;}`,
  `.${C}__tile--lead{grid-column:span 2;aspect-ratio:2/1;}`,
  `@media (min-width:600px){.${C}__gallery{grid-template-columns:repeat(4,minmax(0,1fr));}.${C}__tile--lead{grid-column:span 2;grid-row:span 2;aspect-ratio:auto;}}`
].join("");
function rep(n, part) {
  let out = "";
  for (let i = 0; i < n; i++) out += part(i);
  return out;
}
var card = () => `<div class="${C}__card"><div class="${C}__img"></div><div class="${C}__line"></div><div class="${C}__line ${C}__line--short"></div></div>`;
var month = () => `<div class="${C}__month"><div class="${C}__month-head"><div class="${C}__ctl"></div><div class="${C}__b"></div><div class="${C}__ctl"></div></div><div class="${C}__week">${rep(7, () => `<div class="${C}__dow"></div>`)}</div><div class="${C}__cells">${rep(42, (i) => `<div class="${C}__cell${i < 3 || i > 32 ? ` ${C}__cell--off` : ""}"></div>`)}</div></div>`;
var bar = (modifier = "") => `<div class="${C}__bar${modifier ? ` ${C}__bar--${modifier}` : ""}">${rep(3, () => `<div class="${C}__pill"></div>`)}<div class="${C}__btn"></div></div>`;
var filterRow = (widgetType, options) => widgetType === "explorer" && readPath(options, ["showFilter"]) !== false ? bar("filters") : "";
var SHAPES = {
  // 12 cards: fills three desktop rows (or two rows at up to six columns);
  // anything past the reservation is clipped and faded by the panel.
  grid: (widgetType, options) => `${filterRow(widgetType, options)}<div class="${C}__grid">${rep(12, card)}</div>`,
  "card-row": (widgetType, options) => `${filterRow(widgetType, options)}<div class="${C}__row">${rep(6, card)}</div>`,
  card: () => card(),
  bar: () => bar(),
  form: () => `<div class="${C}__form"><div class="${C}__form-head"><div class="${C}__b"></div><div class="${C}__b"></div></div><div class="${C}__pair"><div class="${C}__field"></div><div class="${C}__field"></div></div><div class="${C}__field"></div><div class="${C}__btn"></div>` + rep(3, () => `<div class="${C}__fee"><div class="${C}__line"></div><div class="${C}__line"></div></div>`) + `</div>`,
  calendar: (widgetType) => `<div class="${C}__months">${rep(widgetType === "multi-calendar" ? 2 : 1, month)}</div>`,
  gallery: () => `<div class="${C}__gallery"><div class="${C}__tile ${C}__tile--lead"></div>${rep(4, () => `<div class="${C}__tile"></div>`)}</div>`
};
var COLUMN_VARS = [
  ["columns", "--hr-preload-cols"],
  ["columns780", "--hr-preload-cols-780"],
  ["columns960", "--hr-preload-cols-960"]
];
function applyColumnVars(panel, options) {
  for (const [key, cssVar] of COLUMN_VARS) {
    const v = readPath(options, ["grid", key]);
    if (typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= 6) {
      panel.style.setProperty(cssVar, String(v));
    }
  }
}
function buildPreloadSkeleton(doc, widgetType, options, heightCss) {
  const variant = resolvePreloadSkeletonVariant(widgetType, options);
  if (!variant) return null;
  const carrier = doc.createElement("div");
  carrier.className = C;
  carrier.setAttribute("data-hr-skeleton", variant);
  carrier.setAttribute("aria-hidden", "true");
  const style = doc.createElement("style");
  style.textContent = PRELOAD_SKELETON_CSS;
  carrier.appendChild(style);
  const panel = doc.createElement("div");
  panel.className = `${C}__panel ${C}__panel--${variant}`;
  panel.style.setProperty("--hr-preload-h", heightCss);
  if (variant === "grid" || variant === "card-row") applyColumnVars(panel, options);
  panel.innerHTML = SHAPES[variant](widgetType, options);
  carrier.appendChild(panel);
  return carrier;
}

// src/runtime/preloader.ts
var STYLE_ID = "hr-widget-preload-style";
var RELEASE_TRANSITION_MS = 250;
var MAX_HOLD_MS = 1e4;
function ensurePreloadStyles(doc) {
  if (doc.getElementById(STYLE_ID)) return;
  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = [
    "[data-hr-preloading]{display:block;background-image:linear-gradient(100deg,rgba(128,128,128,.09) 40%,rgba(128,128,128,.17) 50%,rgba(128,128,128,.09) 60%);background-size:300% 100%;background-position:100% 0;animation:hr-widget-preload 1.4s ease-in-out infinite;border-radius:12px;}",
    "[data-hr-preloading][data-hr-skeleton]{background-image:none;animation:none;}",
    "@keyframes hr-widget-preload{to{background-position:0 0;}}",
    "@media (prefers-reduced-motion: reduce){[data-hr-preloading]{animation:none;}}"
  ].join("\n");
  doc.head.appendChild(style);
}
function resolvePreloadMinHeight(rootEl, widgetType, options) {
  const attr = rootEl.getAttribute("data-hr-min-height")?.trim();
  if (attr) {
    if (/^(0|off|none|false)$/i.test(attr)) return null;
    return /^\d+(\.\d+)?$/.test(attr) ? `${attr}px` : attr;
  }
  return preloadMinHeightHint(widgetType, options);
}
function prefersReducedMotion() {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}
function applyPreloadReservation(rootEl, renderTarget, widgetType, options) {
  const minHeight = resolvePreloadMinHeight(rootEl, widgetType, options);
  if (!minHeight) return null;
  const ownsMinHeight = !rootEl.style.getPropertyValue("min-height");
  const prevTransition = rootEl.style.getPropertyValue("transition");
  const prevTransitionPriority = rootEl.style.getPropertyPriority("transition");
  const restoreTransition = () => {
    if (prevTransition) {
      rootEl.style.setProperty("transition", prevTransition, prevTransitionPriority);
    } else {
      rootEl.style.removeProperty("transition");
    }
  };
  ensurePreloadStyles(rootEl.ownerDocument);
  rootEl.setAttribute("data-hr-preloading", "1");
  if (ownsMinHeight) rootEl.style.minHeight = minHeight;
  const skeleton = buildPreloadSkeleton(
    rootEl.ownerDocument,
    widgetType,
    options,
    ownsMinHeight ? minHeight : rootEl.style.getPropertyValue("min-height")
  );
  if (skeleton) {
    rootEl.setAttribute("data-hr-skeleton", skeleton.dataset.hrSkeleton ?? "");
    if (renderTarget !== rootEl && renderTarget.parentNode) {
      renderTarget.parentNode.insertBefore(skeleton, renderTarget);
    } else {
      rootEl.insertBefore(skeleton, rootEl.firstChild);
    }
  }
  const removeSkeleton = () => {
    skeleton?.remove();
    rootEl.removeAttribute("data-hr-skeleton");
  };
  const hasContent = () => Array.from(renderTarget.children).some((child) => child !== skeleton);
  let released = false;
  let cleanupTimer = null;
  const release = () => {
    if (released) return;
    released = true;
    observer.disconnect();
    clearTimeout(holdTimer);
    rootEl.removeAttribute("data-hr-preloading");
    removeSkeleton();
    if (!ownsMinHeight) return;
    if (prefersReducedMotion()) {
      rootEl.style.removeProperty("min-height");
      return;
    }
    rootEl.style.transition = `min-height ${RELEASE_TRANSITION_MS}ms ease`;
    void rootEl.offsetHeight;
    rootEl.style.minHeight = "0px";
    cleanupTimer = setTimeout(() => {
      rootEl.style.removeProperty("min-height");
      restoreTransition();
    }, RELEASE_TRANSITION_MS + 50);
  };
  const observer = new MutationObserver(() => {
    if (hasContent()) release();
  });
  observer.observe(renderTarget, { childList: true });
  const holdTimer = setTimeout(release, MAX_HOLD_MS);
  if (hasContent()) release();
  return () => {
    released = true;
    observer.disconnect();
    clearTimeout(holdTimer);
    if (cleanupTimer) clearTimeout(cleanupTimer);
    rootEl.removeAttribute("data-hr-preloading");
    removeSkeleton();
    if (ownsMinHeight) {
      rootEl.style.removeProperty("min-height");
      restoreTransition();
    }
  };
}
var FEED_STALE_MS = 5 * 60 * 1e3;
function feedQueryKey(feedId) {
  return ["feed", feedId];
}
function loadFeed(queryClient, feedId) {
  return queryClient.fetchQuery({
    queryKey: feedQueryKey(feedId),
    queryFn: () => fetchFeed(feedId),
    staleTime: FEED_STALE_MS,
    retry: false
  });
}
function useFetchWidget(feedId, widgetId) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["widget", feedId, widgetId],
    queryFn: async () => {
      const [feed, widget] = await Promise.all([
        loadFeed(queryClient, feedId),
        fetchWidget(widgetId)
      ]);
      return { feed, widget };
    },
    // Fail fast on the top-level config fetch (#679): a 404 from an asset/API
    // env mismatch never self-heals, so don't sit through TanStack's default
    // 4-attempt backoff and leave the host blank. One retry covers a transient
    // blip; after that the wrapper surfaces a visible error state. A 429 is
    // the exception (#1930): it is retried on the rate-limit ladder, honouring
    // `Retry-After` when Central sends it, before the notice shows (#740).
    retry: retryTransientOrThrottled(1),
    retryDelay: retryDelayFor
  });
}
function WidgetErrorState({
  rateLimited,
  feedId,
  widgetId,
  notFoundProperty,
  renderFailed
}) {
  const kind = notFoundProperty ? "property-not-found" : renderFailed ? "render-failed" : rateLimited ? "rate-limited" : "widget-fetch-failed";
  const amber = kind === "rate-limited";
  const style = {
    padding: "10px 14px",
    font: "13px/1.45 system-ui, -apple-system, sans-serif",
    borderRadius: 6,
    borderWidth: 1,
    borderStyle: "solid"
  };
  const lightCss = amber ? "color:#92400e;background:#fffbeb;border-color:#fde68a;" : "color:#4b5563;background:#f9fafb;border-color:#e5e7eb;";
  const darkCss = amber ? "color:#fbbf24;background:#451a03;border-color:#92400e;" : "color:#9ca3af;background:#1f2937;border-color:#374151;";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      role: "alert",
      className: "hr-widget-error-state",
      "data-hr-error": kind,
      "data-hr-feed-id": feedId,
      "data-hr-widget-id": widgetId,
      style,
      children: [
        /* @__PURE__ */ jsx("style", { children: `.hr-widget-error-state{${lightCss}}:host([data-hr-theme="dark"]) .hr-widget-error-state,[data-hr-theme="dark"] .hr-widget-error-state{${darkCss}}` }),
        kind === "property-not-found" ? `Property \u201C${notFoundProperty}\u201D wasn\u2019t found in this feed.` : amber ? "This page is loading a lot of content right now and hit a temporary rate limit. Please wait a few seconds and refresh." : "This content couldn\u2019t load. Please refresh the page."
      ]
    }
  );
}
var WidgetRenderBoundary = class extends Component {
  state = { failed: false };
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidUpdate(prevProps) {
    if (this.state.failed && prevProps.resetKey !== this.props.resetKey) {
      this.props.hostEl?.removeAttribute("data-hr-error");
      this.setState({ failed: false });
    }
  }
  componentDidCatch(error) {
    const { widgetType, widgetId, feedId, hostEl } = this.props;
    console.error(
      `[hr-widget] "${widgetType}" render failed (widget ${widgetId ?? "?"}, feed ${feedId ?? "?"}) \u2014 rendering the error state instead of a blank node.`,
      error
    );
    hostEl?.setAttribute("data-hr-error", "render-failed");
    const themeHost = this.props.themeEl ?? hostEl;
    if (themeHost && !themeHost.hasAttribute("data-hr-theme")) {
      try {
        const dark = typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches;
        themeHost.setAttribute("data-hr-theme", dark ? "dark" : "light");
      } catch {
      }
    }
  }
  render() {
    if (this.state.failed) {
      return /* @__PURE__ */ jsx(
        WidgetErrorState,
        {
          renderFailed: true,
          rateLimited: false,
          feedId: this.props.feedId,
          widgetId: this.props.widgetId
        }
      );
    }
    return this.props.children;
  }
};
function ConfigLoader({
  feedId,
  widgetId,
  children,
  optionsOverride,
  rootElement,
  widgetType
}) {
  const [updatedOptions, setUpdatedOptions] = useState();
  const {
    data: widgetData,
    isLoading: isLoadingWidget,
    isError: isErrorWidget,
    error: widgetError
  } = useFetchWidget(feedId, widgetId);
  const rateLimited = isRateLimitError(widgetError);
  useEffect(() => {
    const handler = (e) => {
      setUpdatedOptions(e.detail);
    };
    rootElement.addEventListener("options-updated", handler);
    return () => {
      rootElement.removeEventListener("options-updated", handler);
    };
  }, [rootElement]);
  const finalizedOptions = useMemo(() => {
    if (!widgetData?.widget || !widgetData?.feed) return {};
    const widgetForResolve = updatedOptions ? { ...widgetData.widget, settings: updatedOptions } : widgetData.widget;
    return getFinalSettings(widgetForResolve, widgetData.feed, optionsOverride);
  }, [widgetData?.feed, widgetData?.widget, optionsOverride, updatedOptions]);
  const shadowHost = useShadowHost();
  const markerHost = shadowHost ?? rootElement;
  const hasConfig = Boolean(widgetData);
  const resolvedScheme = widgetData ? finalizedOptions.colorScheme : void 0;
  useEffect(() => {
    if (!hasConfig) return;
    if (!resolvedScheme) {
      markerHost.removeAttribute(HOST_THEME_ATTR);
      return;
    }
    return attachHostThemeController(markerHost, resolvedScheme);
  }, [markerHost, hasConfig, resolvedScheme]);
  useEffect(() => {
    if (isErrorWidget) {
      rootElement.setAttribute(
        "data-hr-error",
        rateLimited ? "rate-limited" : "widget-fetch-failed"
      );
    } else {
      rootElement.removeAttribute("data-hr-error");
    }
  }, [isErrorWidget, rateLimited, rootElement]);
  if (isLoadingWidget) return null;
  if (isErrorWidget) {
    return /* @__PURE__ */ jsx(WidgetErrorState, { rateLimited, feedId, widgetId });
  }
  if (!widgetData) return null;
  return /* @__PURE__ */ jsx(
    WidgetRenderBoundary,
    {
      widgetType: widgetType ?? "csr-widget",
      widgetId,
      feedId,
      hostEl: rootElement,
      themeEl: markerHost,
      resetKey: updatedOptions,
      children: children(widgetData, finalizedOptions)
    }
  );
}
function ClientWrapperForWidget({
  children,
  feedId,
  widgetId,
  optionsOverride,
  rootElement,
  widgetType
}) {
  return /* @__PURE__ */ jsx(QueryClientProvider, { client: getSharedQueryClient(), children: /* @__PURE__ */ jsx(
    ConfigLoader,
    {
      feedId,
      rootElement,
      widgetId,
      optionsOverride,
      widgetType,
      children: (widgetData, finalizedOptions) => children(
        { feed: widgetData.feed, widget: widgetData.widget },
        finalizedOptions
      )
    }
  ) });
}

// src/urls.ts
var PLUGIN_WIDGET_PREFIX = "plugin:";
function parsePluginKeyword(keyword) {
  if (!keyword.startsWith(PLUGIN_WIDGET_PREFIX)) return null;
  const registryKey = keyword.slice(PLUGIN_WIDGET_PREFIX.length);
  if (!registryKey) return null;
  const sep = registryKey.indexOf(":");
  if (sep === -1) {
    return { pluginSlug: registryKey, widgetSlug: null, registryKey };
  }
  const pluginSlug = registryKey.slice(0, sep);
  const widgetSlug = registryKey.slice(sep + 1);
  if (!pluginSlug || !widgetSlug) return null;
  return { pluginSlug, widgetSlug, registryKey };
}
function pluginWidgetDistFile(parts, kind) {
  const base = parts.widgetSlug ?? parts.pluginSlug;
  const dir = parts.widgetSlug ? `${parts.widgetSlug}/` : "";
  const file = `${base}.css` ;
  return `${dir}${file}`;
}
var DEFAULT_PROXY_BASE_URL = "https://assets.homerunner.io";
function getPluginAssetUrl(slug, file, proxyBaseUrl = DEFAULT_PROXY_BASE_URL) {
  const base = proxyBaseUrl.endsWith("/") ? proxyBaseUrl.slice(0, -1) : proxyBaseUrl;
  return `${base}/p/${slug}/${file}`;
}

// src/runtime/registry.ts
function registerWidget(type, registration) {
  if (typeof window === "undefined") return;
  window.HRWidgets = window.HRWidgets || {};
  window.HRWidgets[type] = registration;
  const waiters = window.HRWidgetWaiters?.[type];
  if (waiters && waiters.length) {
    window.HRWidgetWaiters[type] = [];
    for (const resolve of waiters) {
      try {
        resolve(registration);
      } catch {
      }
    }
  }
}
function getRegisteredWidget(type) {
  if (typeof window === "undefined") return void 0;
  const direct = window.HRWidgets?.[type];
  if (direct) return direct;
  if (type.startsWith(PLUGIN_WIDGET_PREFIX)) {
    const key = type.slice(PLUGIN_WIDGET_PREFIX.length);
    const plugin = window.HRPlugins?.[key];
    if (plugin?.component) {
      return plugin.category ? { component: plugin.component, category: plugin.category } : { component: plugin.component };
    }
  }
  return void 0;
}
var REGISTER_TIMEOUT_MS = 1e4;
function whenWidgetRegistered(type) {
  const existing = getRegisteredWidget(type);
  if (existing) return Promise.resolve(existing);
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (reg) => {
      if (settled) return;
      settled = true;
      resolve(reg);
    };
    if (typeof window !== "undefined") {
      window.HRWidgetWaiters = window.HRWidgetWaiters || {};
      (window.HRWidgetWaiters[type] = window.HRWidgetWaiters[type] || []).push(finish);
    }
    const start = Date.now();
    const poll = () => {
      if (settled) return;
      const reg = getRegisteredWidget(type);
      if (reg) return finish(reg);
      if (Date.now() - start >= REGISTER_TIMEOUT_MS) {
        settled = true;
        reject(
          new Error(
            `[widget-core] widget "${type}" did not register within ${REGISTER_TIMEOUT_MS}ms`
          )
        );
        return;
      }
      setTimeout(poll, 16);
    };
    setTimeout(poll, 16);
  });
}

// src/runtime/load-script.ts
function loadScriptOnce(src) {
  if (typeof document === "undefined") return Promise.resolve();
  const store = window.__hrLoadedScripts = window.__hrLoadedScripts || /* @__PURE__ */ new Map();
  const cached = store.get(src);
  if (cached) return cached;
  const promise = new Promise((resolve, reject) => {
    const selector = `script[src="${src.replace(/["\\]/g, "\\$&")}"]`;
    if (document.querySelector(selector)) {
      resolve();
      return;
    }
    const el = document.createElement("script");
    el.src = src;
    el.async = true;
    el.addEventListener("load", () => resolve(), { once: true });
    el.addEventListener(
      "error",
      () => reject(new Error(`[widget-core] failed to load script: ${src}`)),
      { once: true }
    );
    (document.head || document.documentElement).appendChild(el);
  });
  store.set(src, promise);
  return promise;
}
var LAYOUT_KEYWORD_SUFFIX = "-layout";
function buildSlotChildOptions(args) {
  const property = args.property?.trim();
  return JSON.stringify({
    __parentColorScheme: args.colorScheme ?? "light",
    ...property ? { filter: { feed_id: args.feedId, property, platform: null } } : {}
  });
}
function LayoutCsrRenderer(props) {
  return /* @__PURE__ */ jsx(QueryClientProvider, { client: getSharedQueryClient(), children: /* @__PURE__ */ jsx(LayoutCsrInner, { ...props }) });
}
function LayoutCsrInner({
  feedId,
  widgetId,
  LayoutComponent,
  widgetType,
  optionsOverride,
  resolveChildJsUrl,
  rootElement
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useFetchWidget(feedId, widgetId);
  const layoutWidget = data?.widget;
  const feed = data?.feed;
  const [liveOptions, setLiveOptions] = useState();
  useEffect(() => {
    if (!rootElement) return;
    const handler = (e) => setLiveOptions(e.detail);
    rootElement.addEventListener("options-updated", handler);
    return () => rootElement.removeEventListener("options-updated", handler);
  }, [rootElement]);
  const effectiveSettings = liveOptions ?? layoutWidget?.settings;
  const layoutFinal = useMemo(() => {
    if (!layoutWidget || !feed) return null;
    const widgetForResolve = liveOptions ? { ...layoutWidget, settings: liveOptions } : layoutWidget;
    return getFinalSettings(widgetForResolve, feed, optionsOverride);
  }, [layoutWidget, feed, liveOptions, optionsOverride]);
  const pushedProperty = layoutFinal?.filter?.property?.trim() || void 0;
  const shadowHost = useShadowHost();
  const markerHost = shadowHost ?? rootElement ?? null;
  const hasLayoutConfig = Boolean(layoutFinal);
  const layoutScheme = layoutFinal?.colorScheme;
  useEffect(() => {
    markerHost?.setAttribute("data-hr-widget-category", "layout");
  }, [markerHost]);
  useEffect(() => {
    if (!markerHost || !hasLayoutConfig) return;
    if (!layoutScheme) {
      markerHost.removeAttribute("data-hr-theme");
      return;
    }
    return attachHostThemeController(markerHost, layoutScheme);
  }, [markerHost, hasLayoutConfig, layoutScheme]);
  const rawSlots = effectiveSettings?.slots ?? {};
  const slotsKey = JSON.stringify(rawSlots);
  const rawSlotsRef = useRef(rawSlots);
  rawSlotsRef.current = rawSlots;
  const [resolvedSlots, setResolvedSlots] = useState(null);
  const [invalidProperty, setInvalidProperty] = useState(null);
  useEffect(() => {
    if (!layoutWidget || !feed) return;
    let cancelled = false;
    (async () => {
      const slots = {};
      const keywords = /* @__PURE__ */ new Set();
      const validation = pushedProperty ? queryClient.fetchQuery({
        queryKey: ["layout-property-check", feedId, pushedProperty],
        queryFn: () => fetchPropertyBySlug(feedId, pushedProperty),
        retry: retryTransient(1)
      }).then(() => null).catch((err) => {
        if (!isPermanentError(err)) {
          console.warn(
            `[layout-csr] Could not verify property "${pushedProperty}"; rendering the layout anyway.`,
            err
          );
          return null;
        }
        console.error(
          `[layout-csr] Property "${pushedProperty}" was not found in feed ${feedId}. Check the layout's Property setting, or the data-hr-options filter.property on the embed.`,
          err
        );
        return pushedProperty;
      }) : Promise.resolve(null);
      for (const [slotName, ids] of Object.entries(rawSlotsRef.current)) {
        slots[slotName] = [];
        for (const childId of ids ?? []) {
          try {
            const childData = await queryClient.fetchQuery({
              queryKey: ["widget", feedId, childId],
              queryFn: async () => ({ feed, widget: await fetchWidget(childId) })
            });
            const keyword = childData.widget.keyword;
            if (keyword.endsWith(LAYOUT_KEYWORD_SUFFIX)) {
              console.warn(
                `[layout-csr] Skipping nested layout child "${keyword}" (${childId}) in slot "${slotName}".`
              );
              continue;
            }
            slots[slotName].push({ childId, keyword });
            keywords.add(keyword);
          } catch (err) {
            console.warn(
              `[layout-csr] Slot "${slotName}" child ${childId} failed to resolve; skipping.`,
              err
            );
          }
        }
      }
      await Promise.all(
        Array.from(keywords).map(async (keyword) => {
          try {
            if (resolveChildJsUrl) await loadScriptOnce(resolveChildJsUrl(keyword));
            await whenWidgetRegistered(keyword);
          } catch (err) {
            console.warn(
              `[layout-csr] Child widget "${keyword}" failed to load/register; its slots stay empty.`,
              err
            );
          }
        })
      );
      const invalid = await validation;
      if (!cancelled) {
        setInvalidProperty(invalid);
        setResolvedSlots(slots);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    slotsKey,
    pushedProperty,
    layoutWidget,
    feed,
    feedId,
    resolveChildJsUrl,
    queryClient
  ]);
  const optionsAttr = useMemo(
    () => buildSlotChildOptions({
      colorScheme: layoutFinal?.colorScheme,
      feedId,
      property: pushedProperty
    }),
    [layoutFinal?.colorScheme, feedId, pushedProperty]
  );
  const renderedSlots = useMemo(() => {
    if (!resolvedSlots) return {};
    const out = {};
    for (const [slotName, children] of Object.entries(resolvedSlots)) {
      out[slotName] = children.map(({ childId, keyword }) => /* @__PURE__ */ jsx("div", { "data-hr-widget-container": true, children: /* @__PURE__ */ jsx(
        "div",
        {
          "data-hr-widget": keyword,
          id: `${childId}:${feedId}`,
          "data-hr-options": optionsAttr
        }
      ) }, `${childId}:${optionsAttr}`));
    }
    return out;
  }, [resolvedSlots, feedId, optionsAttr]);
  const containerRef = useRef(null);
  const mountedChildrenRef = useRef(/* @__PURE__ */ new Set());
  useEffect(() => {
    if (!resolvedSlots || !containerRef.current) return;
    const hosts = containerRef.current.querySelectorAll(
      "[data-hr-widget-container]"
    );
    const current = /* @__PURE__ */ new Set();
    hosts.forEach((host) => {
      const inner = host.querySelector("[data-hr-widget]");
      const keyword = inner?.getAttribute("data-hr-widget");
      if (!inner || !keyword) return;
      const reg = getRegisteredWidget(keyword);
      if (!reg) return;
      if (reg.category === "layout") {
        console.warn(
          `[layout-csr] Skipping nested plugin layout child "${keyword}" \u2014 layouts cannot nest.`
        );
        return;
      }
      if (reg.mount) reg.mount(host);
      else mount(host, { widget: reg.component, widgetType: keyword });
      current.add(inner);
    });
    const discarded = [...mountedChildrenRef.current].filter((prev) => !current.has(prev));
    mountedChildrenRef.current = current;
    if (discarded.length) {
      queueMicrotask(() => {
        for (const prev of discarded) {
          if (prev.__hrWidgetUnmount) prev.__hrWidgetUnmount();
          else
            console.warn(
              `[HRWidget] discarded slot child "${prev.getAttribute("data-hr-widget") ?? "?"}" (id ${prev.getAttribute("id") ?? "?"}) has no unmount handle (stale bundle or custom mount) \u2014 its widget tree may stay orphaned.`
            );
        }
      });
    }
  }, [resolvedSlots, renderedSlots]);
  useEffect(
    () => () => {
      const remaining = [...mountedChildrenRef.current];
      mountedChildrenRef.current = /* @__PURE__ */ new Set();
      queueMicrotask(() => {
        for (const el of remaining) {
          if (!el.isConnected) el.__hrWidgetUnmount?.();
        }
      });
    },
    []
  );
  const canRenderContainer = !isLoading && !isError && !invalidProperty && Boolean(layoutWidget) && Boolean(feed) && Boolean(layoutFinal);
  useEffect(() => {
    if (canRenderContainer || mountedChildrenRef.current.size === 0) return;
    const remaining = [...mountedChildrenRef.current];
    mountedChildrenRef.current = /* @__PURE__ */ new Set();
    queueMicrotask(() => {
      for (const el of remaining) {
        if (!el.isConnected) el.__hrWidgetUnmount?.();
      }
    });
  }, [canRenderContainer]);
  if (isLoading) return null;
  if (isError) {
    return /* @__PURE__ */ jsx(WidgetErrorState, { rateLimited: isRateLimitError(error), feedId, widgetId });
  }
  if (invalidProperty) {
    return /* @__PURE__ */ jsx(
      WidgetErrorState,
      {
        rateLimited: false,
        notFoundProperty: invalidProperty,
        feedId,
        widgetId
      }
    );
  }
  if (!layoutWidget || !feed || !layoutFinal) return null;
  const resolvedTheme = resolveHostTheme(layoutFinal.colorScheme);
  return /* @__PURE__ */ jsx("div", { ref: containerRef, style: { display: "contents" }, children: /* @__PURE__ */ jsx(
    LayoutComponent,
    {
      options: layoutFinal,
      renderedSlots,
      feedId: feed.id,
      widgetId: layoutWidget.id,
      resolvedTheme,
      widgetType,
      data: void 0
    }
  ) });
}
function extractWidgetId(stringId) {
  const [widgetId, feedId] = stringId.split(":");
  return { widgetId: widgetId ?? "", feedId: Number(feedId) };
}
function isViteDev() {
  return typeof __HR_WIDGET_DEV__ !== "undefined" && __HR_WIDGET_DEV__ === true;
}
function getProxyBaseUrlOverride() {
  if (typeof window === "undefined") return void 0;
  const rt = window.HRWidgetRuntime;
  return rt?.proxyBaseUrl || void 0;
}
function defaultResolveCssUrls(widgetType) {
  const parts = parsePluginKeyword(widgetType);
  if (parts) {
    const cssFile = pluginWidgetDistFile(parts);
    if (isViteDev()) {
      return [`/dist/${parts.pluginSlug}/${cssFile}`];
    }
    return [getPluginAssetUrl(parts.pluginSlug, cssFile, getProxyBaseUrlOverride())];
  }
  return [];
}
function safeJsonParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}
function getRegistryEntry(ssrId) {
  if (!ssrId || typeof window === "undefined") return null;
  const hr = window.HRWidget;
  const entry = hr?.__WIDGET_PROPS__?.[ssrId];
  return entry && typeof entry === "object" ? entry : null;
}
function cssIdentityKey(url) {
  try {
    return new URL(url, "http://x").pathname.replace(/^\/(w|dist)\//, "").replace(/-[0-9a-f]{8}(\.css)$/, "$1");
  } catch {
    return url;
  }
}
function resolveMountCssUrls(config, pageProvided, resolveDefaults, widgetType) {
  const base = config.cssUrls ?? pageProvided;
  const resolvedBase = base.length > 0 ? base : resolveDefaults(widgetType);
  const baseKeys = new Set(resolvedBase.map(cssIdentityKey));
  const extras = (config.extraCssUrls ?? []).filter((u) => !baseKeys.has(cssIdentityKey(u)));
  return [...extras, ...resolvedBase];
}
var HOISTABLE_TAGS = /* @__PURE__ */ new Set(["LINK", "META", "TITLE", "STYLE", "BASE", "SCRIPT"]);
function hasRenderedChildren(el) {
  return Array.from(el.children).some((c) => !HOISTABLE_TAGS.has(c.tagName));
}
function mount(container, config) {
  const allRootEls = container.querySelectorAll(
    `[data-hr-widget="${config.widgetType}"]`
  );
  const resolveDefaults = config.resolveDefaultCssUrls ?? defaultResolveCssUrls;
  allRootEls.forEach((rootEl) => {
    try {
      mountOne(rootEl, container, config, resolveDefaults);
    } catch (error) {
      console.error(
        `[hr-widget] "${config.widgetType}" mount failed for one embed \u2014 other embeds continue.`,
        error
      );
      rootEl.setAttribute("data-hr-error", "mount-failed");
    }
  });
}
function mountOne(rootEl, container, config, resolveDefaults) {
  {
    if (rootEl.hasAttribute("data-hr-mounted")) return;
    rootEl.setAttribute("data-hr-mounted", "1");
    const effectiveCategory = config.category ?? (config.widgetType.startsWith(PLUGIN_WIDGET_PREFIX) ? getRegisteredWidget(config.widgetType)?.category : void 0);
    const hasSSR = rootEl !== container && hasRenderedChildren(rootEl);
    const detailedWidgetId = rootEl.getAttribute("id");
    const ssrIdAttr = rootEl.getAttribute("data-hr-ssr-id");
    const registry = getRegistryEntry(ssrIdAttr);
    const ssrPropsAttr = rootEl.getAttribute("data-hr-ssr-props");
    const ssrProps = registry?.props ?? (ssrPropsAttr ? safeJsonParse(ssrPropsAttr) ?? {} : null);
    const optionsAttr = rootEl.getAttribute("data-hr-options");
    const optionsOverrideExtracted = registry?.options ?? (optionsAttr ? safeJsonParse(optionsAttr) ?? {} : void 0);
    if (!detailedWidgetId) {
      throw new Error("Widget ID is required");
    }
    if (effectiveCategory === "layout" && hasSSR) {
      return;
    }
    const { widgetId, feedId } = extractWidgetId(detailedWidgetId);
    const widgetType = config.widgetType;
    const cssUrls = resolveMountCssUrls(
      config,
      registry?.css && registry.css.length > 0 ? registry.css : getCSSUrlsFromElement(rootEl),
      resolveDefaults,
      widgetType
    );
    const useShadowDOM = config.shadowDOM !== false;
    let shadowSetup = null;
    let isDsd = false;
    if (useShadowDOM && isShadowDOMSupported()) {
      shadowSetup = adoptDeclarativeShadowRoot(rootEl);
      isDsd = shadowSetup !== null;
      if (shadowSetup && isDsd && shadowSetup.cssPending) {
        shadowSetup.reactRoot.style.visibility = "hidden";
      }
      if (!shadowSetup) {
        shadowSetup = setupShadowDOM(rootEl, cssUrls);
        if (shadowSetup && hasSSR) {
          shadowSetup.reactRoot.style.visibility = "hidden";
          migrateSSRContent(rootEl, shadowSetup.reactRoot);
        }
      }
    }
    const hasSsrContent = isDsd ? hasRenderedChildren(shadowSetup.reactRoot) : hasSSR;
    const renderTarget = shadowSetup?.reactRoot ?? rootEl;
    const portalContainer = shadowSetup?.portalContainer;
    const shadowHost = shadowSetup?.shadowHost ?? null;
    let preloadDispose = null;
    if (!hasSsrContent && effectiveCategory !== "layout") {
      preloadDispose = applyPreloadReservation(
        rootEl,
        renderTarget,
        widgetType,
        optionsOverrideExtracted
      );
    }
    let root = null;
    const themeDisposers = [];
    let disposed = false;
    rootEl.__hrWidgetUnmount = () => {
      disposed = true;
      preloadDispose?.();
      preloadDispose = null;
      themeDisposers.forEach((d) => d());
      themeDisposers.length = 0;
      root?.unmount();
      root = null;
      if (shadowSetup) {
        shadowSetup.reactRoot.replaceChildren();
        shadowSetup.reactRoot.style.visibility = "";
      }
      rootEl.removeAttribute("data-hr-mounted");
      delete rootEl.__hrWidgetUnmount;
    };
    const withRenderBoundary = (node) => /* @__PURE__ */ jsx(
      WidgetRenderBoundary,
      {
        widgetType,
        widgetId,
        feedId,
        hostEl: rootEl,
        themeEl: shadowHost ?? rootEl,
        children: node
      }
    );
    const wrapWithProviders = (node) => {
      let wrapped = node;
      if (portalContainer) {
        wrapped = /* @__PURE__ */ jsx(PortalContainerProvider, { value: portalContainer, children: wrapped });
      }
      if (shadowHost) {
        wrapped = /* @__PURE__ */ jsx(ShadowHostProvider, { value: shadowHost, children: wrapped });
      }
      return wrapped;
    };
    const onDeferredMountError = (error) => {
      console.error(
        `[hr-widget] "${widgetType}" deferred mount step failed (widget ${widgetId}, feed ${feedId}).`,
        error
      );
      rootEl.setAttribute("data-hr-error", "mount-failed");
    };
    const render = (forcedOptions) => {
      if (hasSsrContent && !root && ssrProps) {
        themeDisposers.push(
          attachHostThemeController(
            shadowHost ?? rootEl,
            ssrProps.options?.colorScheme,
            {
              fallback: ssrProps.resolvedTheme === "dark" ? "dark" : "light"
            }
          )
        );
        const Widget = config.widget;
        const ssrNode = /* @__PURE__ */ jsx(
          WidgetHydrationTree,
          {
            queryClient: getSharedQueryClient(),
            portalContainer,
            shadowHost,
            children: withRenderBoundary(/* @__PURE__ */ jsx(Widget, { ...ssrProps }))
          }
        );
        const doHydrate = () => {
          if (disposed) return;
          if (shadowSetup) shadowSetup.reactRoot.style.visibility = "";
          root = hydrateRoot(renderTarget, ssrNode, {
            // Matches the server's renderToString identifierPrefix (the ssrId).
            // Legacy pages (no data-hr-ssr-id) predate the isolated server
            // render, so their ids can't match anyway — fall back to default.
            ...ssrIdAttr ? { identifierPrefix: ssrIdAttr } : {}
          });
        };
        if (shadowSetup) shadowSetup.cssReady.then(doHydrate).catch(onDeferredMountError);
        else doHydrate();
        return;
      }
      if (ssrProps) {
        const serverScheme = ssrProps.options?.colorScheme;
        const serverTheme = ssrProps.resolvedTheme;
        const concrete = serverScheme === "dark" || serverScheme === "light" ? serverScheme : !serverScheme && (serverTheme === "dark" || serverTheme === "light") ? serverTheme : null;
        if (concrete) applyHostTheme(shadowHost ?? rootEl, concrete);
      }
      const doRender = () => {
        if (disposed) return;
        if (shadowSetup) shadowSetup.reactRoot.style.visibility = "";
        if (!root) root = createRoot(renderTarget);
        if (effectiveCategory === "layout") {
          root.render(
            wrapWithProviders(
              withRenderBoundary(
                /* @__PURE__ */ jsx(
                  LayoutCsrRenderer,
                  {
                    feedId,
                    widgetId,
                    LayoutComponent: config.widget,
                    widgetType,
                    optionsOverride: optionsOverrideExtracted,
                    resolveChildJsUrl: config.resolveChildJsUrl ?? (typeof window !== "undefined" ? window.HRWidgetRuntime?.resolveChildJsUrl : void 0),
                    rootElement: rootEl
                  }
                )
              )
            )
          );
          return;
        }
        const Widget = config.widget;
        const clientNode = wrapWithProviders(
          withRenderBoundary(
            /* @__PURE__ */ jsx(
              ClientWrapperForWidget,
              {
                optionsOverride: optionsOverrideExtracted,
                feedId,
                widgetId,
                rootElement: rootEl,
                widgetType,
                children: (widgetData, finalizedOptions) => /* @__PURE__ */ jsx(
                  Widget,
                  {
                    feedId: widgetData.feed.id,
                    widgetId: widgetData.widget.id,
                    options: finalizedOptions,
                    resolvedTheme: resolveHostTheme(
                      (finalizedOptions).colorScheme
                    ),
                    widgetType,
                    data: void 0
                  }
                )
              }
            )
          )
        );
        root.render(clientNode);
      };
      if (shadowSetup && !root) shadowSetup.cssReady.then(doRender).catch(onDeferredMountError);
      else doRender();
    };
    render();
  }
}
var sharedPortalHost = null;
var refCount = 0;
function acquirePortalHost() {
  if (!sharedPortalHost) {
    sharedPortalHost = document.createElement("div");
    sharedPortalHost.className = "hr-dialog-portal-host";
    sharedPortalHost.style.position = "fixed";
    sharedPortalHost.style.top = "0";
    sharedPortalHost.style.left = "0";
    sharedPortalHost.style.width = "0";
    sharedPortalHost.style.height = "0";
    sharedPortalHost.style.overflow = "visible";
    sharedPortalHost.style.zIndex = "99999";
    document.body.appendChild(sharedPortalHost);
  }
  refCount += 1;
  return sharedPortalHost;
}
function releasePortalHost() {
  refCount -= 1;
  if (refCount <= 0 && sharedPortalHost) {
    sharedPortalHost.remove();
    sharedPortalHost = null;
    refCount = 0;
  }
}
function useTopLevelPortal(options = {}) {
  const shadowHost = useShadowHost();
  const cssString = options.cssString;
  const enabled = options.enabled !== false;
  const [container, setContainer] = useState(void 0);
  const styleRef = useRef(null);
  const wrapperRef = useRef(null);
  useEffect(() => {
    if (!enabled || !shadowHost) return;
    const originalShadowRoot = shadowHost.shadowRoot;
    if (!originalShadowRoot) return;
    const host = acquirePortalHost();
    const wrapper = document.createElement("div");
    host.appendChild(wrapper);
    wrapperRef.current = wrapper;
    const shadow = wrapper.attachShadow({ mode: "open" });
    const stopThemeMirror = mirrorHostTheme(shadowHost, wrapper);
    const links = originalShadowRoot.querySelectorAll('link[rel="stylesheet"]');
    links.forEach((link) => {
      shadow.appendChild(link.cloneNode(true));
    });
    const styleEl = document.createElement("style");
    shadow.appendChild(styleEl);
    styleRef.current = styleEl;
    const portalDiv = document.createElement("div");
    portalDiv.className = "hr-dialog-portal-root";
    shadow.appendChild(portalDiv);
    setContainer(portalDiv);
    return () => {
      stopThemeMirror();
      wrapper.remove();
      wrapperRef.current = null;
      styleRef.current = null;
      releasePortalHost();
      setContainer(void 0);
    };
  }, [shadowHost, enabled]);
  useEffect(() => {
    if (styleRef.current) {
      styleRef.current.textContent = cssString ?? "";
    }
  }, [cssString, enabled]);
  return container;
}

// src/runtime/visitor-prefs.ts
var PREFIX = "hr-vpref:";
var VISITOR_PREF_TTL_MS = 72 * 60 * 60 * 1e3;
function readVisitorPref(scope, name) {
  try {
    const key = PREFIX + scope + ":" + name;
    const raw = localStorage.getItem(key);
    if (!raw) return void 0;
    const env = JSON.parse(raw);
    if (!env || typeof env !== "object" || typeof env.exp !== "number" || env.exp <= Date.now()) {
      localStorage.removeItem(key);
      return void 0;
    }
    localStorage.setItem(
      key,
      JSON.stringify({ v: env.v, exp: Date.now() + VISITOR_PREF_TTL_MS })
    );
    return env.v;
  } catch {
    return void 0;
  }
}
function writeVisitorPref(scope, name, value) {
  try {
    localStorage.setItem(
      PREFIX + scope + ":" + name,
      JSON.stringify({ v: value, exp: Date.now() + VISITOR_PREF_TTL_MS })
    );
  } catch {
  }
}

export { ClientWrapperForWidget, FEED_STALE_MS, HOST_THEME_ATTR, HR_COMPACT_REF_KEY, HR_COMPACT_VERSION, LayoutCsrRenderer, MIN_SHARED_BYTES, PortalContainerProvider, RATE_LIMIT_BACKOFF_MS, RATE_LIMIT_RETRY_CAP_MS, ShadowHostProvider, VISITOR_PREF_TTL_MS, WidgetErrorState, WidgetFetchError, WidgetHydrationTree, WidgetRenderBoundary, applyHostTheme, attachHostThemeController, dedupeDehydratedState, extractWidgetId, feedQueryKey, fetchFeed, fetchWidget, getCSSUrlsFromElement, getFinalSettings, getRegisteredWidget, getSharedQueryClient, homerunnerApiBaseUrl, inflateDehydratedState, isCompactDehydratedState, isPermanentError, isRateLimitError, isRetryableRateLimit, isShadowDOMSupported, isWidgetFetchError, loadFeed, loadScriptOnce, migrateSSRContent, mirrorHostTheme, mount, parseRetryAfterMs, readVisitorPref, registerWidget, resolveHostTheme, retryDelayFor, retryTransient, retryTransientOrThrottled, setupShadowDOM, useFetchWidget, usePortalContainer, useShadowHost, useTopLevelPortal, whenWidgetRegistered, writeVisitorPref };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map